import React, { useState } from "react";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Image,
  Type,
  AlignLeft,
  MousePointerClick,
  ChevronRight
} from "lucide-react";

function TemplateEditor({ template, onBack, onNext }) {
  const containerRef = React.useRef(null);
  const [sidebarWidth, setSidebarWidth] = useState(720);
  const [isResizing, setIsResizing] = useState(false);
  const [editData, setEditData] = useState(template || {
    id: null,
    name: "My Campaign",
    previewTitle: "",
    previewContent: "",
    previewButton: "Click Here",
    previewButtons: [],
    bgColor: "#f4f4f5",
    image: "🎁"
  });

  const [showButtonInputs, setShowButtonInputs] = useState(false);
  const [newButton, setNewButton] = useState({ name: "Click here", url: "" });

  const startResizing = React.useCallback(() => setIsResizing(true), []);
  const stopResizing = React.useCallback(() => setIsResizing(false), []);

  const resize = React.useCallback((e) => {
    if (isResizing && containerRef.current) {
      const newWidth = e.clientX - containerRef.current.getBoundingClientRect().left;
      const maxWidth = window.innerWidth - 100;
      if (newWidth > 320 && newWidth < maxWidth) setSidebarWidth(newWidth);
    }
  }, [isResizing]);

  React.useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", resize);
      window.addEventListener("mouseup", stopResizing);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    } else {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, resize, stopResizing]);

  const handleChange = (field, value) =>
    setEditData((prev) => ({ ...prev, [field]: value }));

  const handleAddButton = () => {
    if (newButton.name.trim()) {
      setEditData((prev) => ({
        ...prev,
        previewButtons: [...(prev.previewButtons || []), newButton],
      }));
      setNewButton({ name: "Click here", url: "" });
      setShowButtonInputs(false);
    }
  };

  const removeButton = (idx) =>
    setEditData((prev) => ({
      ...prev,
      previewButtons: prev.previewButtons.filter((_, i) => i !== idx),
    }));

  const COLOR_PRESETS = [
    "#ffffff", "#f4f4f5", "#fee2e2", "#ffedd5", "#fef3c7",
    "#dcfce7", "#e0e7ff", "#ede9fe", "#fae8ff", "#18181b"
  ];

  return (
    <div
      className="flex flex-col font-['Inter',sans-serif]"
      style={{ height: "calc(100vh - 84px)", background: "#ffffff" }}
      ref={containerRef}
    >
      {/* ── Header ── */}
      <header className="h-16 bg-white border-b border-zinc-200 flex items-center justify-between px-6 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="w-8 h-8 flex items-center justify-center border border-zinc-200 rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 transition-colors cursor-pointer flex-shrink-0"
            title="Back"
          >
            <ArrowLeft size={15} />
          </button>
          <div>
            <input
              type="text"
              value={editData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Campaign name"
              className="text-sm sm:text-base font-bold text-zinc-900 tracking-tight bg-transparent border-b border-transparent hover:border-zinc-300 focus:border-zinc-900 focus:outline-none transition-colors"
            />
            <p className="text-xs text-zinc-500 mt-0.5">Customize in-app campaign appearance and messaging.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="px-3.5 py-1.5 rounded-lg text-xs font-semibold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 border border-zinc-200 transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onNext(editData)}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors bg-zinc-900 text-white hover:bg-zinc-800 cursor-pointer shadow-xs active:scale-[0.98]"
          >
            <span>Next Step</span>
            <ChevronRight size={14} />
          </button>
        </div>
      </header>

      {/* ── Body: Sidebar + Resizer + Preview ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* ── Left Sidebar ── */}
        <aside
          className="bg-white border-r border-zinc-200 overflow-y-auto flex-shrink-0"
          style={{ width: sidebarWidth }}
        >
          <div className="p-6 sm:p-8 space-y-6">
            {/* Section header */}
            <div className="border-b border-zinc-100 pb-3">
              <h2 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">Appearance</h2>
              <p className="text-xs text-zinc-500 mt-0.5">Customize the look and content of your campaign card.</p>
            </div>

            {/* Cover Image */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-zinc-700 flex items-center gap-1.5">
                <Image size={13} className="text-zinc-500" />
                <span>Cover Image or Emoji</span>
              </label>
              <div
                className="h-32 rounded-xl border border-dashed border-zinc-300 flex items-center justify-center cursor-pointer relative overflow-hidden hover:border-zinc-400 transition-colors group"
                style={{ background: editData.bgColor }}
                onClick={() => document.getElementById("te-file-input").click()}
              >
                {editData.image && (editData.image.startsWith("data:") || editData.image.includes("/") || editData.image.includes(".webp") || editData.image.includes("static")) ? (
                  <img src={editData.image} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-4xl select-none">{editData.image || "✨"}</span>
                )}
                <div className="absolute inset-0 bg-zinc-900/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-xl">
                  <span className="text-white text-xs font-semibold">Upload Image</span>
                </div>
              </div>
              <input id="te-file-input" type="file" className="hidden" onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (ev) => handleChange("image", ev.target.result);
                  reader.readAsDataURL(file);
                }
              }} accept="image/*" />

              {/* Emoji shortcut */}
              <input
                type="text"
                value={editData.image && !editData.image.startsWith("data:") ? editData.image : ""}
                onChange={(e) => handleChange("image", e.target.value)}
                placeholder="Or type an emoji e.g. 🎁"
                className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 hover:bg-zinc-50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 placeholder:text-zinc-400 transition-colors shadow-2xs font-medium"
              />

              {/* Background color */}
              <div className="pt-2">
                <label className="text-[11px] font-medium text-zinc-500 block mb-2">Background Color</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {COLOR_PRESETS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => handleChange("bgColor", c)}
                      className={`w-6 h-6 rounded-md border transition-transform flex-shrink-0 cursor-pointer ${
                        editData.bgColor === c ? "border-zinc-900 ring-2 ring-zinc-900/20 scale-110" : "border-zinc-300 hover:border-zinc-400"
                      }`}
                      style={{ background: c }}
                    />
                  ))}
                  <input
                    type="color"
                    value={editData.bgColor}
                    onChange={(e) => handleChange("bgColor", e.target.value)}
                    className="w-6 h-6 rounded-md border border-zinc-200 cursor-pointer p-0.5 bg-white"
                    title="Custom color"
                  />
                </div>
              </div>
            </div>

            {/* Display Title */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700 flex items-center gap-1.5">
                <Type size={13} className="text-zinc-500" />
                <span>Display Title</span>
              </label>
              <input
                value={editData.previewTitle}
                onChange={(e) => handleChange("previewTitle", e.target.value)}
                placeholder="e.g. Special Offer!"
                className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 hover:bg-zinc-50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 placeholder:text-zinc-400 transition-colors shadow-2xs"
              />
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700 flex items-center gap-1.5">
                <AlignLeft size={13} className="text-zinc-500" />
                <span>Description</span>
              </label>
              <textarea
                value={editData.previewContent}
                onChange={(e) => handleChange("previewContent", e.target.value)}
                placeholder="Tell your visitors more..."
                rows={4}
                className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 hover:bg-zinc-50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 placeholder:text-zinc-400 transition-colors shadow-2xs resize-none"
              />
            </div>

            {/* Call to Action */}
            <div className="bg-zinc-50/60 p-4 rounded-xl border border-zinc-200/80 space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-zinc-700 flex items-center gap-1.5">
                  <MousePointerClick size={13} className="text-zinc-500" />
                  <span>Call to Action</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowButtonInputs(true)}
                  className="flex items-center gap-1 text-xs font-semibold text-zinc-700 hover:text-zinc-900 transition-colors cursor-pointer"
                >
                  <Plus size={12} />
                  <span>Add Button</span>
                </button>
              </div>

              {(editData.previewButtons || []).length > 0 ? (
                <div className="space-y-2">
                  {editData.previewButtons.map((btn, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-white px-3 py-2 rounded-lg border border-zinc-200 shadow-2xs">
                      <span className="flex-1 text-xs font-medium text-zinc-800 truncate">{btn.name}</span>
                      <button
                        type="button"
                        onClick={() => removeButton(idx)}
                        className="text-zinc-400 hover:text-red-600 transition-colors cursor-pointer"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <input
                  value={editData.previewButton}
                  onChange={(e) => handleChange("previewButton", e.target.value)}
                  placeholder="Button text"
                  className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 placeholder:text-zinc-400 transition-colors shadow-2xs"
                />
              )}

              {showButtonInputs && (
                <div className="mt-3 p-3 bg-white rounded-lg border border-zinc-200 space-y-2.5 shadow-2xs">
                  <input
                    className="w-full px-3 py-1.5 text-xs border border-zinc-200 rounded-md bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 transition-colors"
                    placeholder="Button label"
                    value={newButton.name}
                    onChange={(e) => setNewButton({ ...newButton, name: e.target.value })}
                  />
                  <input
                    className="w-full px-3 py-1.5 text-xs border border-zinc-200 rounded-md bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 transition-colors"
                    placeholder="URL (Optional)"
                    value={newButton.url}
                    onChange={(e) => setNewButton({ ...newButton, url: e.target.value })}
                  />
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={handleAddButton}
                      className="flex-1 py-1.5 bg-zinc-900 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors cursor-pointer"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowButtonInputs(false)}
                      className="px-3 py-1.5 border border-zinc-200 text-zinc-600 text-xs font-semibold rounded-md hover:bg-zinc-50 transition-colors cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* ── Resizer ── */}
        <div
          className={`w-1 flex-shrink-0 cursor-col-resize relative group transition-colors ${isResizing ? "bg-zinc-900" : "bg-zinc-200 hover:bg-zinc-400"}`}
          onMouseDown={startResizing}
        />

        {/* ── Preview Panel ── */}
        <main className="flex-1 flex flex-col items-center justify-center p-8 bg-zinc-50/50 overflow-auto">
          <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-5">Live Widget Preview</span>
          <div className="w-[340px] bg-white rounded-2xl shadow-lg shadow-zinc-200/60 overflow-hidden border border-zinc-200/80">
            {/* Image area */}
            <div
              className="h-44 flex items-center justify-center overflow-hidden"
              style={{ background: editData.bgColor }}
            >
              {editData.image && (editData.image.startsWith("data:") || editData.image.includes("/") || editData.image.includes(".webp") || editData.image.includes("static")) ? (
                <img src={editData.image} alt="Cover" className="w-full h-full object-cover" />
              ) : (
                <span className="text-4xl select-none">{editData.image || "✨"}</span>
              )}
            </div>

            {/* Content */}
            <div className="p-6 text-center">
              <h3 className="text-base font-bold text-zinc-900 tracking-tight mb-2">
                {editData.previewTitle || "Your Title"}
              </h3>
              <p className="text-xs text-zinc-500 leading-relaxed mb-5">
                {editData.previewContent || "Your description goes here."}
              </p>
              {(editData.previewButtons || []).length > 0 ? (
                <div className="space-y-2">
                  {editData.previewButtons.map((btn, idx) => (
                    <button key={idx} type="button" className="w-full py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs font-semibold text-zinc-800 cursor-default">
                      {btn.name}
                    </button>
                  ))}
                </div>
              ) : (
                <button type="button" className="w-full py-2.5 bg-zinc-900 text-white rounded-lg text-xs font-semibold cursor-default hover:bg-zinc-800 shadow-xs">
                  {editData.previewButton || "Learn More"}
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default TemplateEditor;
